import React, { useState, useMemo, useEffect } from 'react';
import useSWR from 'swr'
import { useGlobal, useUser } from 'src/context';
import { getVoteStatus } from 'src/api';
import { firstRoundCandidates } from './static';
import NominateDialog from './NominateDialog';

const NominateSection = () => {
  const [nominatingSection, setNominatingSection] = useState(null);

  const { onOpenLogin } = useGlobal();
  const { isLogin } = useUser();

  const { data, isLoading, mutate } = useSWR(`goty2024-voteStatus`, getVoteStatus);

  useEffect(() => {
    if (isLogin) {
      mutate();
    }
  }, [isLogin]);

  const voteStatus = data?.data || {};

  const isAllNominated = useMemo(() => {
    return voteStatus.result && Object.keys(voteStatus.result).length === 11;
  }, [voteStatus]);

  const isEventOpening = useMemo(() => {
    return voteStatus.event && voteStatus.event === "OPEN_FIRST";
  }, [voteStatus]);

  const description = useMemo(() => {
    if (isLoading) return '';
    if (!voteStatus.event) return '';

    if (voteStatus.event === "WAITING_FIRST") return '活動尚未開始';
    if (voteStatus.event === "OPEN_FIRST") {
      return isLogin ? (
        voteStatus.isVotable ? '您符合提名資格，請點擊下方項目進行提名。' : '第一階段進行中，請靜待其他玩家提名。'
      ) : (
        '請先登入確認是否符合提名資格。'
      );
    }
    if (voteStatus.event === "WAITING_SECOND") return '請靜待第二階段開始';
    if (voteStatus.event === "OPEN_SECOND") {
      return isLogin ? (
        voteStatus.isVotable ? '請點擊下方項目進行投票' : '您不符合投票資格，可以瀏覽投票狀況。'
      ) : (
        '請先登入確認是否符合投票資格。'
      );
    }
    if (voteStatus.event === "END") return '第二階段已結束，請靜待得獎名單公布。';
    return '';
  }, [voteStatus, isLoading, isLogin]);

  const handleOpenDialog = (sectionKey) => {
    const isNominated = voteStatus.result[sectionKey]?.createdAt;
    if (isNominated) return;
    if (!isEventOpening) return;
    
    setNominatingSection(sectionKey);
  };

  const handleCloseDialog = () => {
    setNominatingSection(null);
  };

  return (
    <section id="nominate" className="py-10 bg-[#317E70]">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-white mb-4">第一階段提名</h2>

        <p className='mb-10 text-center text-white text-lg font-bold'>{description}</p>

        {isLogin && voteStatus?.isVotable && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {Object.entries(firstRoundCandidates).map(([key, category], index) => {
              const isNominated = voteStatus.result[key]?.createdAt;
              const nominations = voteStatus.result[key]?.value || [];
              
              return (
                <div 
                  key={key}
                  className={`bg-[#7B2337] p-6 rounded-lg shadow-lg text-white flex flex-col transform transition-transform duration-300 ${isNominated ? 'cursor-default' : 'cursor-pointer hover:scale-105'}`}
                  onClick={() => handleOpenDialog(key)}
                >
                  <h3 className="text-2xl font-bold text-[#DFB750] mb-4">{index + 1}. {category.name}</h3>
                  {isNominated ? (
                    <div className='flex flex-col items-center'>
                      <span className='bg-[#DFB750] text-white text-sm font-semibold px-3 py-1 rounded-full mb-2'>已提名</span>
                      <div className='flex flex-col items-center gap-2 text-sm font-semibold'>
                        {nominations.map(item => <span key={item}>《{item}》</span>)}
                      </div>
                    </div>
                  ) : (
                    <div className='flex justify-center'>
                      <span className={`inline-block bg-[#DD6B47] text-white text-sm font-semibold px-3 py-1 rounded-full ${isEventOpening ? 'cursor-pointer' : 'cursor-default'}`}>
                        {isEventOpening ? '可提名' : '已結束'}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
            {isAllNominated && (
              <div className='bg-[#7B2337] p-6 rounded-lg shadow-lg flex flex-col justify-center items-center cursor-default text-white text-xl font-bold'>
                <p>您已完成所有提名</p>
                <p>感謝您的踴躍參與</p>
                <p>請靜待入圍名單公布</p>
              </div>
            )}
          </div>
        )}
      </div>

      <NominateDialog
        isOpen={!!nominatingSection}
        onClose={handleCloseDialog}
        sectionKey={nominatingSection}
      />
    </section>
  );
};

export default NominateSection;
